import * as v from 'valibot'

/**
 * A studio announcement: a title and a Markdown body an administrator
 * writes for everyone. It reaches each active account twice — a line in
 * the bell, and once as a full card the first time they open the console.
 * `seenAt` is the viewer's own: when they opened it. `viewers` is everyone
 * who has read it, the author aside — the studio's confirmation it landed.
 */
export interface AnnouncementView {
  id: string
  title: string
  /** Markdown — headings, bold, italics, lists, links and images by URL. */
  body: string
  authorName: string | null
  createdAt: string
  updatedAt: string
  archivedAt: string | null
  /** When it was pinned. At most one live announcement is pinned; it stays on top and on Home, read or not. */
  pinnedAt: string | null
  seenAt: string | null
  viewers: AnnouncementViewer[]
  /** The poll, when the announcement asks one. */
  poll: AnnouncementPoll | null
}

/**
 * A poll on an announcement: fixed options, one vote each, changeable until
 * closed. `voters` is filled for administrators only; everyone else reads the
 * counts and their own choice.
 */
export interface AnnouncementPoll {
  options: { id: string; label: string; votes: number; voters: AnnouncementViewer[] | null }[]
  total: number
  myVote: string | null
  closedAt: string | null
}

/** One person who read an announcement, and when. */
export interface AnnouncementViewer {
  id: string
  name: string | null
  email: string
  pictureUrl: string | null
  seenAt: string
}

const text = (max: number) =>
  v.pipe(v.string(), v.trim(), v.minLength(1, 'This field is required.'), v.maxLength(max, `Use at most ${max} characters.`))

export const createAnnouncementSchema = v.object({
  title: text(140),
  body: text(20_000),
  poll: v.optional(v.object({
    options: v.pipe(
      v.array(text(80)),
      v.minLength(2, 'A poll needs at least two options.'),
      v.maxLength(6, 'A poll takes at most six options.'),
      v.check((options) => new Set(options.map((option) => option.toLowerCase())).size === options.length, 'Each option must be different.'),
    ),
  })),
})

export type CreateAnnouncementInput = v.InferOutput<typeof createAnnouncementSchema>

export const updateAnnouncementSchema = v.pipe(
  v.object({
    title: v.optional(text(140)),
    body: v.optional(text(20_000)),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateAnnouncementInput = v.InferOutput<typeof updateAnnouncementSchema>

export const voteSchema = v.object({ optionId: v.pipe(v.string(), v.trim(), v.minLength(1)) })

export type VoteInput = v.InferOutput<typeof voteSchema>
